import React, { useCallback } from 'react';
import { Modal, Table, Input, Button, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FJ741 } from '../../../services/j219Api';

interface WashLabelModalProps {
  visible: boolean;
  isEditing: boolean;
  details: FJ741[];
  onClose: () => void;
  onUpdateDetails: (details: FJ741[]) => void;
}

const WashLabelModal: React.FC<WashLabelModalProps> = React.memo(({
  visible, isEditing, details, onClose, onUpdateDetails,
}) => {
  const addRow = useCallback(() => {
    const newSeq = details.length > 0 ? Math.max(...details.map(d => d.fj741_02)) + 1 : 1;
    onUpdateDetails([...details, { fj741_01: '', fj741_02: newSeq, fj741_11: '', fj741_12: '' }]);
  }, [details, onUpdateDetails]);

  const removeRow = useCallback((idx: number) => {
    const updated = details.filter((_, i) => i !== idx).map((d, i) => ({ ...d, fj741_02: i + 1 }));
    onUpdateDetails(updated);
  }, [details, onUpdateDetails]);

  const updateField = useCallback((idx: number, field: keyof FJ741, value: string) => {
    const updated = details.map((d, i) => (i === idx ? { ...d, [field]: value } : d));
    onUpdateDetails(updated);
  }, [details, onUpdateDetails]);

  const columns = [
    {
      title: <span data-contract-field="fj741_02">項次</span>,
      dataIndex: 'fj741_02',
      width: 60,
      render: (_: number, __: FJ741, idx: number) => idx + 1,
    },
    {
      title: <span data-contract-field="fj741_11">洗標代號</span>,
      dataIndex: 'fj741_11',
      width: 150,
      render: (val: string, _: FJ741, idx: number) =>
        isEditing ? (
          <Input value={val} size="small" onChange={e => updateField(idx, 'fj741_11', e.target.value)} data-contract-field="fj741_11" />
        ) : val,
    },
    {
      title: <span data-contract-field="fj741_12">洗標說明</span>,
      dataIndex: 'fj741_12',
      render: (val: string, _: FJ741, idx: number) =>
        isEditing ? (
          <Input value={val} size="small" onChange={e => updateField(idx, 'fj741_12', e.target.value)} data-contract-field="fj741_12" />
        ) : val,
    },
    ...(isEditing
      ? [
          {
            title: '操作',
            width: 60,
            render: (_: unknown, __: FJ741, idx: number) => (
              <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeRow(idx)} />
            ),
          },
        ]
      : []),
  ];

  return (
    <Modal
      title={`洗標明細 (${details.length} 項)`}
      open={visible}
      onCancel={onClose}
      width={600}
      footer={
        <Space>
          {isEditing && (
            <Button type="dashed" icon={<PlusOutlined />} onClick={addRow}>
              新增洗標
            </Button>
          )}
          <Button onClick={onClose}>關閉</Button>
        </Space>
      }
    >
      <Table
        dataSource={details}
        columns={columns}
        rowKey={(_, idx) => String(idx)}
        pagination={false}
        size="small"
        locale={{ emptyText: '暫無洗標項目' }}
        scroll={{ y: 300 }}
      />
    </Modal>
  );
});

WashLabelModal.displayName = 'WashLabelModal';
export default WashLabelModal;
